import React from 'react'
import PropTypes from 'prop-types'
import { Table, AutoSizer, Column } from 'react-virtualized'
import Loading from '../../views/Loading'

import style from './style.css'

const { Constants : ReduxConstants } = require( '@leverege/api-redux' )
const { QueryUtil } = require( '@leverege/api-redux' )
const classnames = require( 'classnames' )
const Path = require( '@leverege/path' )
const { Button, Dialog } = require( '@leverege/ui-elements' )
const { cleanObject } = require( '../../utils/Export' )

class TableResults extends React.Component {
  static propTypes = {
    results : PropTypes.object,
    query : PropTypes.object.isRequired,
    blueprint : PropTypes.object,
    onEvent : PropTypes.func.isRequired,
    pageSize : PropTypes.number.isRequired
  }

  static defaultProps = {
    results : { items : [] },
    blueprint : {}
  }

  static TYPE = 'table';

  constructor( props, context ) {
    super( props, context )

    this.state = {
      showModal : false,
      modalContents : null
    }
  }

  openModal = ( { data } ) => {
    this.setState( {
      showModal : true,
      modalContents : <pre>{JSON.stringify( data, null, 2 )}</pre>
    } )
  }

  closeModal = () => this.setState( { showModal : false, modalContents : null } )

  pathToObj = ( path ) => {
    if ( typeof path === 'string' ) {
      return {
        path,
        alias : QueryUtil.removeDollar( path ).split( '/' ).slice( -1 )[0]
      }
    }
    return { path : path[0], alias : path[1] }
  }

  canLoadMore = () => {
    const { results } = this.props
    const maxRange = results && results.ranges && results.ranges[results.ranges.length - 1] && results.ranges[results.ranges.length - 1][1]
    const itemLength = results && results.items && results.items.length
    
    return maxRange === itemLength
  }

  isLoading = () => {
    const { results } = this.props
    return results.status === ReduxConstants.QUERY_STATUS_LOADING
  }

  loadMore = () => {
    const { onEvent } = this.props
    onEvent( { type : 'loadMore' }, 'select' )
  }

  checkForLoadMore = ( { overscanStopIndex } ) => {
    const { results } = this.props
    if ( results && results.items && ( overscanStopIndex >= results.items.length - 1 ) && this.canLoadMore() ) {
      this.loadMore()
    }
  }

  rowGetter = ( { index } ) => {
    const { results } = this.props
    return results && results.items && results.items[index]
  }

  columnAccessor = ( { columnData, dataKey, rowData } ) => {
    let val
    if ( columnData && columnData.command === 'cleanObject' ) {
      val = cleanObject( columnData.attributes, Path( dataKey ).get( rowData ) )
    } else {
      val = Path( dataKey ).get( rowData )
    }

    if ( typeof val === 'object' && val != null ) {
      val = (
        <Button className={classnames( style.link, style.center )} eventData={val} onClick={this.openModal}>
          <i className="fa fa-info" />
        </Button>
      )
    }
    return val
  }

  renderCell = ( { columnIndex, key, rowIndex, style : cellStyle } ) => {
    const { results } = this.props
    const rowData = results.items[rowIndex]
    const col = this.columns[columnIndex]
    return (
      <div
        className={classnames( style.tableCell )}
        style={cellStyle}
        key={key}>
        {col.cellDataGetter( { columnData : col.columnData, dataKey : col.dataKey, rowData } )}
      </div>
    )
  }

  renderHeaderCell = ( { columnIndex, key, style : cellStyle } ) => {
    const col = this.columns[columnIndex]
    return (
      <div className={style.headerCell} style={cellStyle} key={key}>
        {col.label}
      </div>
    )
  }

  lazyColumnWidth = ( { path, label, data } ) => {
    const firstRow = data[0]
    const val = Path( path ).get( firstRow )
    const len = val != null ? val.toString().length : 0
    // TODO : make this not totally arbitrary
    return ( Math.max( len, label.length ) * 10 ) + 16
  }

  getColumnWidth = ( { index } ) => {
    const col = this.columns[index]
    const toReturn = ( col && col.width != null ) ? Math.max( col.width, 20 ) : 60
    return toReturn
  }

  getRowClassName( { index } ) {
    if ( index === -1 ) {
      return style.headerRow
    }
    return index % 2 === 0 ? style.evenRow : style.oddRow
  }

  renderPlaceholder = () => {
    const { results } = this.props
    let contents = 'There is no data for the current query'

    if ( results && results.error && results.error.message ) {
      contents = results.error.message
    }

    return (
      <div className={style.tableResults}>
        <div className={style.placeholder}>
          {contents}
        </div>
      </div>
    )
  }

  renderLoadingOverlay = () => {
    return <Loading show />
  }

  render() {
    const { results, query, blueprint } = this.props
    const { showModal, modalContents } = this.state

    let columns = []
    
    // we either need a blueprint or a set of paths to turn into a column set, others we break
    if ( !results || !results.items ) {
      return null
    }

    const data = results.items && results.items.map( item => ( item || {} ) )

    if ( ( !data || data.length === 0 ) && !this.isLoading() ) {
      return this.renderPlaceholder()
    }

    if ( query.paths && query.paths.length > 0 ) {
      // don't use the blueprint to build the table
      columns = query.paths.map( ( path ) => {
        const p = this.pathToObj( path )
        return {
          key : p.path,
          label : p.alias,
          cellDataGetter : this.columnAccessor,
          dataKey : p.alias,
          width : this.lazyColumnWidth( { path : p.alias, label : p.alias, data } )
        }
      } )
    } else if ( blueprint && blueprint.data ) {
      // use the blueprint attributes
      columns = blueprint.data.attributes.map( ( attribute ) => {
        return {
          key : attribute.path,
          label : attribute.name,
          cellDataGetter : this.columnAccessor,
          dataKey : `data/${attribute.path}`,
          width : this.lazyColumnWidth( { path : `data/${attribute.path}`, label : attribute.name, data } )
        }
      } )
      columns.unshift(
        {
          key : 'time',
          label : 'Msg Time',
          cellDataGetter : this.columnAccessor,
          dataKey : 'time',
          width : 200
        }
      )
      columns.push( {
        key : 'notIncludedInBlueprint',
        label : 'Not in Blueprint',
        cellDataGetter : this.columnAccessor,
        dataKey : 'data',
        columnData : { command : 'cleanObject', attributes : blueprint.data.attributes },
        cellRenderer : this.renderCell,
        width : 120
      } )
    }
    
    this.columns = columns
    
    let tWidth = 0
    columns.forEach( ( col ) => {
      tWidth += col.width
    } )
    return (
      <div className={style.tableResults}>
        <AutoSizer>
          {
            ( { height } ) => (
              <Table
                className={style.table}
                rowHeight={40}
                columnWidth={this.getColumnWidth}
                headerHeight={50}
                rowCount={data.length}
                rowGetter={this.rowGetter}
                rowClassName={this.getRowClassName}
                columnCount={columns.length}
                height={height}
                onRowsRendered={this.checkForLoadMore}
                width={tWidth} >
                {
                  columns.map( col => (
                    <Column 
                      {...col} />
                  ) )
                }
              </Table>
            )
          }
        </AutoSizer>
        {this.isLoading() && this.renderLoadingOverlay()}
        <Dialog.Message
          show={showModal}
          onClose={this.closeModal}
          title="Data Not in Device Blueprint">
          {modalContents}
        </Dialog.Message>
      </div>
    )
  }
} 

exports.TableResults = TableResults
